import { resolveApiBaseUrl } from "@/utils/apiBaseUrl.js";

const emitPwaEvent = (name, detail = {}) => {
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

const clearServiceWorkerAndCaches = async () => {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations?.();

    if (registrations?.length) {
      await Promise.all(
        registrations.map((registration) => registration.unregister())
      );
    }

    const cacheKeys = await caches.keys?.();

    if (cacheKeys?.length) {
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug("[pwa] failed to clear service worker/cache", error);
    }
  }
};

const configureServiceWorkerApiBaseUrl = (registration) => {
  const apiBaseUrl = resolveApiBaseUrl();
  const message = { type: "SET_API_BASE_URL", apiBaseUrl };

  registration?.active?.postMessage(message);
  registration?.waiting?.postMessage(message);
  registration?.installing?.postMessage(message);
  navigator.serviceWorker.controller?.postMessage(message);
};

export const registerServiceWorker = () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  // In local dev, service workers cause stale API/cache problems.
  // Disable and clear them automatically.
  if (
    import.meta.env.DEV ||
    import.meta.env.VITE_DISABLE_SERVICE_WORKER === "true"
  ) {
    clearServiceWorkerAndCaches();
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        configureServiceWorkerApiBaseUrl(registration);
        emitPwaEvent("hotel:pwaReady", { registration });

        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
          emitPwaEvent("hotel:pwaUpdateAvailable", { registration });
        }

        registration.addEventListener("updatefound", () => {
          const nextWorker = registration.installing;

          if (!nextWorker) {
            return;
          }

          nextWorker.postMessage({
            type: "SET_API_BASE_URL",
            apiBaseUrl: resolveApiBaseUrl(),
          });

          nextWorker.addEventListener("statechange", () => {
            if (
              nextWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              configureServiceWorkerApiBaseUrl(registration);
              nextWorker.postMessage({ type: "SKIP_WAITING" });
              emitPwaEvent("hotel:pwaUpdateAvailable", { registration });
            }
          });
        });
      })
      .catch((error) => {
        if (import.meta.env.DEV) {
          console.debug("[pwa] service worker registration failed", error);
        }
      });
  });

  let refreshing = false;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) {
      return;
    }

    refreshing = true;
    window.location.reload();
  });
};

export const applyServiceWorkerUpdate = (registration) => {
  registration?.waiting?.postMessage({ type: "SKIP_WAITING" });
};